import { useEffect, useRef, useState } from 'react';

/**
 * Returns a ref and a width state for the provided element.
 * The width is updated whenever the element's size changes.
 * The hook uses the ResizeObserver API to observe size changes.
 * If the browser does not support RO, it also updates on window resize.
 * @returns An object with `ref` and `width` properties.
 * `ref` is a ref to the element.
 * `width` is the width of the element in pixels.
 */
export function useElementWidth<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState<number>(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Only the last pending measure runs, and unmount cancels it (a late
    // setState after unmount threw "window is not defined" in jsdom tests).
    let timer: ReturnType<typeof setTimeout> | undefined;

    /**
     * Sets the width of the element in the state.
     * This function is called once initially to set the width and then whenever the element's size changes.
     */
    const setElWidth = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setWidth(el.getBoundingClientRect().width), 500);
    };

    // Initial measure
    setElWidth();

    // Observe size changes
    const resizeObs = new ResizeObserver(setElWidth);
    resizeObs.observe(el);

    // Also update on window resize (some layouts don’t trigger RO in older browsers)
    window.addEventListener('resize', setElWidth);

    return () => {
      clearTimeout(timer);
      resizeObs.disconnect();
      window.removeEventListener('resize', setElWidth);
    };
  }, []);

  return { ref, width };
}
